<template>
  <Loading v-if="loading" size="100px" />
  <DashboardLayout v-else>
    <template v-slot:title>
      <div class="d-flex flex-column flex-md-row align-items-start justify-content-md-between align-items-md-center mb-3 mb-md-0">
        <h1 class="text-left mb-2 mb-md-0">Users</h1>
        <b-button variant="primary" v-b-modal.modal-create-user>
          Create New User
        </b-button>
      </div>
    </template>

    <template v-slot:body>
      <b-modal id="modal-create-user" title="Create New User" hide-footer>
        <b-form @submit.prevent="onSubmit">
          <b-alert :show="!!createUserError" variant="danger">{{ createUserError }}</b-alert>
          <b-form-group label="Email">
            <b-form-input v-model="form.email" type="email" placeholder="Email" required />
          </b-form-group>
          <b-form-group label="First Name">
            <b-form-input v-model="form.first_name" placeholder="First Name" required />
          </b-form-group>
          <b-form-group label="Last Name">
            <b-form-input v-model="form.last_name" placeholder="Last Name" required />
          </b-form-group>
          <b-form-group label="Role">
            <b-form-select v-model="form.role" :options="createRoleOptions" />
          </b-form-group>
          <b-form-group label="Race">
            <b-form-select v-model="form.race_uuid" :options="createRaceOptions" />
          </b-form-group>
          <b-button variant="primary" type="submit">Create User</b-button>
        </b-form>
      </b-modal>

      <div class="users-filters d-flex flex-wrap mb-3">
        <b-form-input
          v-model="search"
          placeholder="Search..."
          size="sm"
          class="users-filter"
        />
        <b-form-select
          v-model="filterRole"
          :options="roleFilterOptions"
          size="sm"
          class="users-filter"
        />
        <b-form-select
          v-model="filterRace"
          :options="raceFilterOptions"
          size="sm"
          class="users-filter"
        />
        <b-form-select
          v-model="filterState"
          :options="stateOptions"
          size="sm"
          class="users-filter"
        />
      </div>

      <div v-if="selected.length > 0" class="bulk-actions d-flex flex-wrap align-items-center mb-3 p-2 border rounded">
        <span class="mr-2">{{ selected.length }} selected</span>
        <b-button size="sm" variant="outline-success" class="mr-2" :disabled="bulkLoading" @click="bulkActivate">
          Activate
        </b-button>
        <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="bulkLoading" @click="bulkDeactivate">
          Deactivate
        </b-button>
        <b-form-select
          v-model="bulkRaceUuid"
          :options="bulkRaceOptions"
          size="sm"
          class="mr-2"
          style="max-width: 200px"
        />
        <b-button size="sm" variant="outline-primary" class="mr-2" :disabled="bulkLoading" @click="bulkSetRace">
          Set Race
        </b-button>
        <span v-if="bulkLoading" class="mr-2">Working…</span>
        <b-button size="sm" variant="link" :disabled="bulkLoading" @click="clearSelection">
          Clear selection
        </b-button>
      </div>
      <b-alert :show="!!bulkError" variant="danger" dismissible @dismissed="bulkError = null">
        {{ bulkError }}
      </b-alert>

      <b-table striped hover small :items="filteredUsers" :fields="fields">
        <template #head(select)>
          <b-form-checkbox
            :checked="allFilteredSelected"
            @change="toggleSelectAll"
          />
        </template>
        <template #cell(select)="row">
          <b-form-checkbox
            :checked="isSelected(row.item.uuid)"
            @change="toggleSelect(row.item.uuid)"
          />
        </template>
        <template #cell(full_name)="row">
          <div>{{ row.item.full_name }}</div>
          <StateBadge :states="userStates" :stateKey="row.item.state" />
        </template>
        <template #cell(actions)="row">
          <UserStateDropdown
            :user="row.item"
            :allTriggers="allUserTriggers"
            :loading="row.item.loading"
            @trigger-action="handleTriggerAction($event, row.item)"
          />
        </template>
      </b-table>

      <UpdateUserModal :user="pendingUpdateUser" @confirm="onUpdateUserConfirm" />
    </template>
  </DashboardLayout>
</template>

<script>
import Loading from "@/components/renderingComponents/Loading";
import DashboardLayout from "@/components/layoutComponents/DashboardLayout";
import StateBadge from "@/components/renderingComponents/StateBadge";
import UserStateDropdown from "@/components/renderingComponents/UserStateDropdown";
import UpdateUserModal from "@/components/renderingComponents/UpdateUserModal";

import { userData } from "@/reference/UserReferenceData.js";

const ROLES = ["admin", "staff", "volunteer"];

export default {
  name: "UsersPage",
  components: { DashboardLayout, Loading, StateBadge, UserStateDropdown, UpdateUserModal },
  data() {
    return {
      form: { first_name: "", last_name: "", email: "", role: null, race_uuid: null },
      createUserError: null,
      loading: false,
      pendingUpdateUser: null,
      search: "",
      filterState: null,
      filterRole: null,
      filterRace: null,
      selected: [],
      bulkRaceUuid: null,
      bulkLoading: false,
      bulkError: null,
      fields: [
        { key: "select", label: "" },
        { key: "full_name", label: "Name" },
        { key: "primary_group", label: "Role" },
        { key: "assigned_race", label: "Race" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    userStates() { return userData.userStates; },
    allUserTriggers() { return userData.allUserTriggers; },
    allUsers() { return this.$store.state.usersStore.users || []; },
    filteredUsers() {
      const q = this.search.toLowerCase();
      return this.allUsers.filter((u) => {
        if (q && !u.full_name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
        if (this.filterState && u.state !== this.filterState) return false;
        if (this.filterRole && u.primary_group !== this.filterRole) return false;
        if (this.filterRace && u.assigned_race !== this.filterRace) return false;
        return true;
      });
    },
    stateOptions() {
      const states = [...new Set(this.allUsers.map((u) => u.state).filter(Boolean))];
      return [{ value: null, text: "All states" }, ...states.map((s) => ({ value: s, text: s }))];
    },
    roleFilterOptions() {
      const roles = [...new Set(this.allUsers.map((u) => u.primary_group).filter(Boolean))];
      return [{ value: null, text: "All roles" }, ...roles.map((r) => ({ value: r, text: r }))];
    },
    raceFilterOptions() {
      const races = [...new Set(this.allUsers.map((u) => u.assigned_race).filter(Boolean))];
      return [{ value: null, text: "All races" }, ...races.map((r) => ({ value: r, text: r }))];
    },
    createRoleOptions() {
      return [{ value: null, text: "No role" }, ...ROLES.map((r) => ({ value: r, text: r }))];
    },
    createRaceOptions() {
      const races = this.$store.state.racesStore.availableRaces || [];
      return [{ value: null, text: "No race" }, ...races.map((r) => ({ value: r.uuid, text: r.short_name }))];
    },
    bulkRaceOptions() {
      const races = this.$store.state.racesStore.availableRaces || [];
      return [{ value: null, text: "Set race..." }, ...races.map((r) => ({ value: r.uuid, text: r.short_name }))];
    },
    allFilteredSelected() {
      return this.filteredUsers.length > 0 && this.filteredUsers.every((u) => this.selected.includes(u.uuid));
    },
  },
  mounted() {
    this.loading = true;
    this.$store
      .dispatch("usersStore/getAllUsers", this.$store.state.usersStore.me.organisation)
      .then((users) => { this.$store.commit("usersStore/setUsers", users); })
      .catch(() => {})
      .finally(() => { this.loading = false; });

    this.$root.$on("bv::modal::hidden", (bvEvent, modalId) => {
      if (modalId === "modal-create-user") {
        this.form = { first_name: "", last_name: "", email: "", role: null, race_uuid: null };
        this.createUserError = null;
      }
    });
  },
  methods: {
    onSubmit() {
      this.createUserError = null;
      const payload = { ...this.form };
      if (!payload.role) delete payload.role;
      if (!payload.race_uuid) delete payload.race_uuid;
      this.$store
        .dispatch("usersStore/createUser", payload)
        .then((res) => {
          this.$store.commit("usersStore/addUser", res.data);
          this.$bvModal.hide("modal-create-user");
        })
        .catch((error) => {
          const data = error.response?.data;
          this.createUserError = data?.email?.[0] || data?.error || "Failed to create user. Please try again.";
        });
    },
    handleTriggerAction(triggerName, user) {
      if (triggerName === "update_user") {
        this.pendingUpdateUser = user;
        this.$bvModal.show("update-user-modal");
        return;
      }
      this.$set(user, "loading", true);
      this.$store
        .dispatch("usersStore/submitUserTrigger", { userUUID: user.uuid, trigger: triggerName, data: {} })
        .then(() => this.$store.dispatch("usersStore/getAllUsers", this.$store.state.usersStore.me.organisation))
        .then((users) => { this.$store.commit("usersStore/setUsers", users); })
        .catch((error) => { console.error("Trigger action failed:", error); })
        .finally(() => { this.$set(user, "loading", false); });
    },
    onUpdateUserConfirm(payload) {
      const user = this.pendingUpdateUser;
      if (!user) return;
      this.$set(user, "loading", true);
      this.$store
        .dispatch("usersStore/submitUserTrigger", { userUUID: user.uuid, trigger: "update_user", data: payload })
        .then(() => this.$store.dispatch("usersStore/getAllUsers", this.$store.state.usersStore.me.organisation))
        .then((users) => { this.$store.commit("usersStore/setUsers", users); })
        .catch((error) => { console.error("Update user failed:", error); })
        .finally(() => { this.$set(user, "loading", false); });
    },
    isSelected(uuid) {
      return this.selected.includes(uuid);
    },
    toggleSelect(uuid) {
      if (this.selected.includes(uuid)) {
        this.selected = this.selected.filter((id) => id !== uuid);
      } else {
        this.selected = [...this.selected, uuid];
      }
    },
    toggleSelectAll(checked) {
      const filteredUuids = this.filteredUsers.map((u) => u.uuid);
      if (checked) {
        this.selected = [...new Set([...this.selected, ...filteredUuids])];
      } else {
        this.selected = this.selected.filter((id) => !filteredUuids.includes(id));
      }
    },
    clearSelection() {
      this.selected = [];
      this.bulkRaceUuid = null;
    },
    async bulkRunTrigger(trigger, data) {
      this.bulkLoading = true;
      this.bulkError = null;
      const failedNames = [];
      for (const uuid of this.selected) {
        const user = this.allUsers.find((u) => u.uuid === uuid);
        try {
          await this.$store.dispatch("usersStore/submitUserTrigger", { userUUID: uuid, trigger, data });
        } catch (error) {
          console.error(`Bulk ${trigger} failed for ${uuid}:`, error);
          failedNames.push(user ? user.full_name : uuid);
        }
      }
      const users = await this.$store.dispatch("usersStore/getAllUsers", this.$store.state.usersStore.me.organisation);
      this.$store.commit("usersStore/setUsers", users);
      if (failedNames.length > 0) {
        this.bulkError = `Failed for: ${failedNames.join(", ")}`;
      }
      this.clearSelection();
      this.bulkLoading = false;
    },
    bulkActivate() {
      this.bulkRunTrigger("activate", {});
    },
    bulkDeactivate() {
      this.bulkRunTrigger("deactivate", {});
    },
    bulkSetRace() {
      if (this.bulkRaceUuid === null) return;
      this.bulkRunTrigger("update_user", { race_uuid: this.bulkRaceUuid });
    },
  },
};
</script>

<style scoped>
.users-filters {
  gap: 0.5rem;
}
.users-filter {
  flex: 1 1 160px;
  min-width: 140px;
}
</style>
